import { useEffect, useState, useRef } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { LogOut, Upload, Trash2, Check, ImageOff, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, imageUrl } from "@/lib/api";

const Admin = () => {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/gallery");
    setItems(data);
    setLoaded(true);
  };
  useEffect(() => {
    if (user) load();
  }, [user]);

  if (user === null) return null;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choisissez une photo");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("description", description);
    setUploading(true);
    try {
      await api.post("/gallery", fd);
      toast.success("Photo ajoutée");
      setTitle("");
      setDescription("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      toast.error("Échec de l'envoi");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Photo supprimée");
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const saveMeta = async (id, t, d) => {
    try {
      await api.put(`/gallery/${id}`, { title: t, description: d });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, title: t, description: d } : i)));
      toast.success("Enregistré");
    } catch {
      toast.error("Échec");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F1EB] text-[#1C1A18]">
      <Toaster position="top-center" richColors />
      <header className="flex items-center justify-between px-6 md:px-12 h-20 border-b border-[#1C1A18]/10">
        <Link to="/" className="font-serif-el text-2xl">Atelier Vernet</Link>
        <div className="flex items-center gap-6">
          <span className="text-xs uppercase tracking-[0.15em] text-[#5C564D] hidden sm:block">
            {user.email}
          </span>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="flex items-center gap-2 text-sm uppercase tracking-[0.15em] hover:text-[#8B5A33] transition-colors"
          >
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        <h1 className="font-serif-el text-4xl md:text-5xl mb-2">Gérer la galerie</h1>
        <p className="text-[#5C564D] mb-10">
          Ajoutez, décrivez et supprimez vos photos. {items.length} photo{items.length > 1 ? "s" : ""}.
        </p>

        {/* Upload */}
        <form
          onSubmit={upload}
          data-testid="upload-form"
          className="bg-white border border-[#1C1A18]/10 p-6 md:p-8 mb-14 grid md:grid-cols-2 gap-6"
        >
          <div className="space-y-4">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              data-testid="upload-file"
              className="block w-full text-sm file:mr-4 file:py-2.5 file:px-5 file:border-0 file:bg-[#1C1A18] file:text-[#F4F1EB] file:uppercase file:tracking-wider file:text-xs file:cursor-pointer"
            />
            <input
              data-testid="upload-title"
              placeholder="Titre (ex. Buffet en chêne)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border-b border-[#1C1A18]/20 py-2 bg-transparent focus:outline-none focus:border-[#8B5A33]"
            />
          </div>
          <div className="space-y-4 flex flex-col">
            <textarea
              data-testid="upload-description"
              placeholder="Description (essence, dimensions, projet…)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full border border-[#1C1A18]/15 p-3 bg-transparent focus:outline-none focus:border-[#8B5A33] resize-none"
            />
            <button
              data-testid="upload-submit"
              disabled={uploading}
              className="flex items-center justify-center gap-2 bg-[#8B5A33] text-[#F4F1EB] uppercase tracking-[0.15em] text-sm py-3.5 hover:bg-[#1C1A18] transition-colors disabled:opacity-60"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {uploading ? "Envoi…" : "Ajouter la photo"}
            </button>
          </div>
        </form>

        {/* List */}
        {loaded && items.length === 0 && (
          <div className="flex flex-col items-center py-24 text-[#5C564D]">
            <ImageOff size={40} className="mb-4 opacity-50" />
            <p>Aucune photo pour le moment. Ajoutez votre première création.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it) => (
            <AdminCard key={it.id} item={it} onRemove={remove} onSave={saveMeta} />
          ))}
        </div>
      </div>
    </div>
  );
};

const AdminCard = ({ item, onRemove, onSave }) => {
  const [t, setT] = useState(item.title || "");
  const [d, setD] = useState(item.description || "");
  const dirty = t !== (item.title || "") || d !== (item.description || "");
  return (
    <div className="bg-white border border-[#1C1A18]/10 overflow-hidden" data-testid={`admin-item-${item.id}`}>
      <div className="aspect-[4/3] overflow-hidden bg-[#EAE5D9]">
        <img src={imageUrl(item.id)} alt={item.title} className="w-full h-full object-cover" />
      </div>
      <div className="p-4 space-y-3">
        <input
          value={t}
          onChange={(e) => setT(e.target.value)}
          placeholder="Titre"
          className="w-full font-serif-el text-lg border-b border-transparent hover:border-[#1C1A18]/15 focus:border-[#8B5A33] focus:outline-none pb-1"
        />
        <textarea
          value={d}
          onChange={(e) => setD(e.target.value)}
          placeholder="Description"
          rows={2}
          className="w-full text-sm text-[#5C564D] border border-[#1C1A18]/10 p-2 focus:outline-none focus:border-[#8B5A33] resize-none"
        />
        <div className="flex gap-2 pt-1">
          <button
            data-testid={`save-${item.id}`}
            disabled={!dirty}
            onClick={() => onSave(item.id, t, d)}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider py-2.5 bg-[#1C1A18] text-[#F4F1EB] disabled:opacity-30 hover:bg-[#8B5A33] transition-colors"
          >
            <Check size={14} /> Enregistrer
          </button>
          <button
            data-testid={`delete-${item.id}`}
            onClick={() => onRemove(item.id)}
            className="px-4 py-2.5 border border-[#9C4E33] text-[#9C4E33] hover:bg-[#9C4E33] hover:text-[#F4F1EB] transition-colors"
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Admin;
